<template>
    <div ref="wrapper" class="ppv2-relative ppv2-text-left" :style="{ width: widthPx + 'px' }">
        <div
            class="ppv2-flex ppv2-items-center ppv2-gap-[9px] ppv2-cursor-pointer ppv2-transition-colors ppv2-rounded-[10px] ppv2-px-3 ppv2-py-2"
            :class="[
                open
                    ? 'ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-[#7D2DBD] ppv2-shadow-[rgba(125,45,189,0.14)_0px_0px_0px_3px]'
                    : 'ppv2-bg-[#F8F9FB] dark:ppv2-bg-black dark:ppv2-bg-opacity-5 ppv2-border ppv2-border-[#E2E6EC]',
                hasError ? 'ppv2-border-red-500' : '',
                disabled ? 'ppv2-opacity-50 ppv2-pointer-events-none' : '',
            ]"
            @click="open = true"
        >
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9aa4b2"
                stroke-width="2"
                class="ppv2-flex-shrink-0"
            >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
            </svg>

            <input
                ref="input"
                v-model="draft"
                :disabled="disabled"
                inputmode="numeric"
                maxlength="5"
                :title="$t('common.scheduleTime.placeholder')"
                type="text"
                class="ppv2-flex-1 ppv2-min-w-0 ppv2-w-full ppv2-outline-none ppv2-bg-transparent ppv2-text-[14px] poppinsMedium ppv2-text-black ppv2-tabular-nums"
                style="border: none; margin: 0; padding: 0"
                @focus="handleFocus"
                @mouseup="selectTimePart"
                @click.stop
                @input="handleInput"
                @keydown.enter.prevent="commitDraft"
                @keydown.esc.prevent="closeAndRevert"
                @blur="handleBlur"
            />

            <!-- Valeur ≥ 24:00 : rappel « le lendemain » hors de l'input (maxlength 5 conservé) -->
            <span
                v-if="showNextDayBadge && isNextDayValue && !editing"
                class="ppv2-text-[10px] ppv2-text-[#7D2DBD] poppinsMedium ppv2-whitespace-nowrap ppv2-flex-shrink-0"
            >
                {{ nextDayLabel }}
            </span>

            <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9aa4b2"
                stroke-width="2.4"
                stroke-linecap="round"
                class="ppv2-flex-shrink-0 ppv2-transition-transform"
                :class="open ? 'ppv2-rotate-180' : ''"
            >
                <path d="M6 9l6 6 6-6" />
            </svg>
        </div>

        <div
            v-if="open"
            class="ppv2-absolute ppv2-z-[60] ppv2-left-0 ppv2-mt-[6px] ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-[#CBD2DC] ppv2-rounded-[12px] ppv2-shadow-[0px_14px_34px_rgba(2,24,50,0.18)] ppv2-p-2"
            :style="{ width: widthPx + 'px', maxHeight: '250px', overflow: 'auto' }"
            @mousedown.prevent
        >
            <div class="custom-scrollbar" style="display: flex; flex-direction: column; gap: 2px">
                <button
                    v-for="opt in options"
                    :key="opt.key"
                    type="button"
                    :data-sel="opt.value === roundedValue ? '1' : '0'"
                    class="ppv2-text-left ppv2-whitespace-nowrap ppv2-text-[12.5px] ppv2-px-[10px] ppv2-py-[7px] ppv2-rounded-[8px] ppv2-border ppv2-border-transparent ppv2-bg-white dark:ppv2-bg-dark ppv2-text-black ppv2-cursor-pointer ppv2-tabular-nums poppinsMedium"
                    :class="optionClass(opt)"
                    @click="pickOption(opt)"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>

        <p v-if="hasError && showError" class="ppv2-text-[11px] ppv2-text-red-500 ppv2-mt-0.5">{{ errorText }}</p>
    </div>
</template>

<script>
import * as hours from '../../utils/scheduleHours'

/**
 * Sélecteur d'heure (liste au pas de 15 min + saisie clavier).
 * Valeurs en minutes depuis 00:00 ; une valeur ≥ 1440 signifie « le lendemain »
 * (fin de créneau nocturne, jusqu'à 05:00 = 1740). Les options hors [min, max] sont
 * grisées et non cliquables ; une saisie clavier hors bornes est ramenée à la borne.
 * Aucune règle métier ici : les bornes viennent du parent (scheduleHours.weekSlotBounds).
 */
export default {
    name: 'TimeSelect',
    props: {
        value: {
            type: Number,
            default: 0,
        },
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 1439,
        },
        step: {
            type: Number,
            default: 15,
        },
        // Dernière option listée : 1439 pour un début, 1740 (05:00 le lendemain) pour une fin.
        maxOptions: {
            type: Number,
            default: 1439,
        },
        // Début du créneau (minutes) : une fin saisie au clavier avant 12:00 et ≤ ce début
        // est interprétée comme « le lendemain » (ex. « 01:00 » pour un créneau 17:30 → 25:00).
        promoteBelow: {
            type: Number,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        showError: {
            type: Boolean,
            default: true,
        },
        widthPx: {
            type: Number,
            default: 186,
        },
        // Rappel « Lendemain » dans le champ. L'éditeur d'horaires (maquette 5a) ne l'affiche que dans la liste.
        showNextDayBadge: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            open: false,
            draft: '',
            editing: false,
            hasError: false,
        }
    },
    computed: {
        roundedValue() {
            return Math.round(Number(this.value) || 0)
        },
        isNextDayValue() {
            // 1440 s'affiche « 23:59 » (fermeture à minuit), pas comme une heure du lendemain.
            return this.roundedValue > hours.DAY_MINUTES
        },
        nextDayLabel() {
            return this.$t('common.scheduleTime.nextDayShort')
        },
        options() {
            const list = []
            for (let m = 0; m <= this.maxOptions; m += this.step) {
                list.push({
                    key: m,
                    value: m,
                    label: hours.formatTime(m, this.nextDayLabel),
                    disabled: !this.isInRange(m),
                })
            }
            // Valeur hors grille (ex. 20:05) : conservée et épinglée en tête, marquée « saisi ».
            if (this.roundedValue % this.step !== 0 && this.isInRange(this.roundedValue)) {
                list.unshift({
                    key: 'entered-' + this.roundedValue,
                    value: this.roundedValue,
                    label:
                        hours.formatTime(this.roundedValue, this.nextDayLabel) + '  ·  ' + this.$t('common.scheduleTime.entered'),
                    disabled: false,
                })
            }
            return list
        },
        errorText() {
            return this.$t('common.scheduleTime.invalidTime')
        },
    },
    watch: {
        value(newValue) {
            if (!this.editing) {
                this.draft = this.displayText(Math.round(Number(newValue) || 0))
            }
        },
        open(val) {
            if (!val) return
            this.$nextTick(() => {
                const selected = this.$el.querySelector('[data-sel="1"]')
                if (selected) selected.scrollIntoView({ block: 'nearest' })
            })
        },
    },
    mounted() {
        this.draft = this.displayText(this.roundedValue)
        document.addEventListener('mousedown', this.handleOutsideClick)
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.handleOutsideClick)
    },
    methods: {
        displayText(minutes) {
            return hours.formatTime(minutes)
        },
        optionClass(opt) {
            const isSelected = opt.value === this.roundedValue
            return {
                '!ppv2-border-[#7D2DBD] !ppv2-bg-[#F3EAFA] !ppv2-text-[#571F84] poppinsSemiBold': isSelected,
                // Hors bornes : grisé, curseur « interdit » ; le clic est ignoré par pickOption().
                'time-option--off': opt.disabled && !isSelected,
            }
        },
        isInRange(value) {
            return value >= Math.min(this.min, this.max) && value <= Math.max(this.min, this.max)
        },
        clampToRange(minutes) {
            const lo = Math.round(Math.min(this.min, this.max))
            const hi = Math.round(Math.max(this.min, this.max))
            return Math.min(Math.max(Math.round(minutes), lo), hi)
        },
        pickOption(opt) {
            if (opt.disabled) return
            this.emitCommit(opt.value)
            this.open = false
        },
        emitCommit(minutes) {
            this.hasError = false
            this.editing = false
            this.draft = this.displayText(minutes)
            if (minutes === this.roundedValue) return
            this.$emit('input', minutes)
            this.$emit('change', minutes)
        },
        handleFocus() {
            this.editing = true
            this.open = true
            this.selectTimePart({ target: this.$refs.input })
        },
        selectTimePart(event) {
            const el = event && event.target
            const inputEl = this.$refs.input
            if (!el || !inputEl || el !== inputEl) return
            const value = el.value || ''
            const sep = value.indexOf(':')
            if (sep < 0) return

            // Au premier clic le navigateur n'a pas encore positionné le caret à l'endroit
            // du clic (action par défaut du clic), donc selectionStart est périmé (0) et
            // on sélectionnerait les heures même en cliquant sur les minutes. On se base
            // sur la position réelle du clic quand elle est disponible.
            let start
            let end
            if (event.clientX != null) {
                const idx = this.charIndexAtPoint(el, event.clientX)
                if (idx >= 0) {
                    start = idx <= sep ? 0 : sep + 1
                    end = idx <= sep ? sep : value.length
                }
            }
            if (start === undefined) {
                const pos = typeof el.selectionStart === 'number' ? el.selectionStart : 0
                start = pos <= sep ? 0 : sep + 1
                end = pos <= sep ? sep : value.length
            }
            this.selectRange(inputEl, start, end)
        },
        /** Index du caractère sous le clic, ou -1 si non déterminable. */
        charIndexAtPoint(el, clientX) {
            try {
                const rect = el.getBoundingClientRect()
                if (clientX < rect.left || clientX > rect.right) return -1
                const ctx = this.measureCtx(window.getComputedStyle(el))
                const value = el.value
                const x = clientX - rect.left
                let acc = 0
                for (let i = 0; i < value.length; i++) {
                    const w = ctx.measureText(value[i]).width
                    if (x <= acc + w / 2) return i
                    acc += w
                }
                return value.length
            } catch (e) {
                return -1
            }
        },
        measureCtx(style) {
            if (!this._measureCtx) {
                const canvas = document.createElement('canvas')
                this._measureCtx = canvas.getContext('2d')
            }
            const font = style.font ? style.font : `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
            if (this._measureFont !== font) {
                this._measureCtx.font = font
                this._measureFont = font
            }
            return this._measureCtx
        },
        selectRange(el, start, end) {
            this.$nextTick(() => {
                try {
                    el.setSelectionRange(start, end)
                } catch (e) {
                    // noop
                }
            })
        },
        handleInput() {
            this.editing = true
            this.hasError = false
        },
        commitDraft() {
            let parsed = hours.hhmmToMinutes(this.draft)
            if (parsed === null) {
                this.hasError = true
                if (document.activeElement === this.$refs.input) {
                    this.$refs.input.focus()
                }
                return
            }
            // Fin saisie « avant » le début du créneau (et le matin) → le lendemain.
            if (
                this.promoteBelow !== null &&
                parsed < hours.DAY_MINUTES &&
                parsed < 720 &&
                parsed <= this.promoteBelow % hours.DAY_MINUTES
            ) {
                parsed += hours.DAY_MINUTES
            }
            // Les minutes sont toujours un multiple de 5 (ex. 21/22 → 20, 23/24 → 25).
            // Le snap porte sur le total en minutes pour gérer les reports d'heure (23:59 → 24:00).
            const snapped = hours.snapToGrid(parsed, 5)
            this.emitCommit(this.clampToRange(snapped))
            this.open = false
        },
        closeAndRevert() {
            this.editing = false
            this.open = false
            this.hasError = false
            this.draft = this.displayText(this.roundedValue)
        },
        handleBlur() {
            this.editing = false
            this.commitDraft()
            this.open = false
        },
        handleOutsideClick(event) {
            if (this.$refs.wrapper && !this.$refs.wrapper.contains(event.target)) {
                this.open = false
            }
        },
    },
}
</script>
<style lang="scss" scoped>
button.ppv2-text-black {
    color: rgba(2, 24, 50, var(--tw-text-opacity));
}
button.time-option--off,
button.time-option--off:hover {
    color: #cbd2dc;
    background: #f8f9fb;
    cursor: not-allowed;
}
.dark button.time-option--off {
    color: rgba(255, 255, 255, 0.25) !important;
    background: transparent;
}
.dark button.ppv2-text-black {
    color: rgba(255, 255, 255, var(--tw-text-opacity)) !important;
}
</style>
